import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type PropsFor} from "@khanacademy/wonder-blocks-core";
import Log, {Errors} from "@khan/logging";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import Error from "#logged-in-homepage-package/content-pages/error.tsx";
import {
    profileLessFiles,
    type Profile,
} from "#logged-in-homepage-package/content-pages/profile-util.ts";
import type {Badge, UserBadge} from "#profile-package/get-badges.ts";

import BadgeSpotlight from "./badge-spotlight.tsx";

type OwnProps = {
    kaid: string;
    profilePromise?: Promise<Profile>;
    onSufficientlyUsable?: () => void;
    spotlightBadgeSlug: string | null | undefined;
};

type Props = OwnProps & WithApolloProps;

type State = {
    spotlightBadge: Badge | null | undefined;
    loading: boolean;
    showError: boolean;
};

class BadgesViewImpl extends React.Component<Props, State> {
    state: State = {
        spotlightBadge: null,
        loading: true,
        showError: false,
    };

    componentDidMount() {
        this.updateLegacyAchievements();
    }

    componentDidUpdate(prevProps: Props) {
        if (
            prevProps.kaid !== this.props.kaid ||
            prevProps.spotlightBadgeSlug !== this.props.spotlightBadgeSlug
        ) {
            this.updateLegacyAchievements();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _isMounted: boolean = false;

    updateLegacyAchievements() {
        const {profilePromise, onSufficientlyUsable, spotlightBadgeSlug} =
            this.props;
        this._isMounted = true;

        const promises: Array<Promise<any>> = [
            import("../profile-package/profile-tabs.tsx").then(
                (m) => m.default,
            ),
            profilePromise ? profilePromise : Promise.resolve(),
            ...profileLessFiles(),
        ];

        Promise.all(promises)
            // @ts-expect-error - TS2345 - Argument of type '([ProfileTabs]: [any]) => void' is not assignable to parameter of type '(value: any[]) => void | PromiseLike<void>'.
            .then(([ProfileTabs]: [any]) => {
                if (!this._isMounted) {
                    return;
                }

                this.setState({loading: false});

                ProfileTabs.populateAchievements(
                    this.props.gqlClient,
                    null,
                    "lg",
                    true,
                    spotlightBadgeSlug,
                ).then((spotlightBadge?: UserBadge | null) => {
                    const newSpotlightBadge = spotlightBadge
                        ? spotlightBadge.badge
                        : null;
                    if (
                        this._isMounted &&
                        newSpotlightBadge !== this.state.spotlightBadge
                    ) {
                        this.setState({
                            spotlightBadge: newSpotlightBadge,
                        });
                    }
                    if (onSufficientlyUsable) {
                        onSufficientlyUsable();
                    }
                });
            })
            .catch((error) => {
                if (this._isMounted) {
                    Log.error(
                        "Error initializing Badges profile:",
                        Errors.Internal,
                        {cause: error},
                    );
                    this.setState({showError: true});
                }
            });
    }

    render(): React.ReactNode {
        const {loading, showError, spotlightBadge} = this.state;

        if (showError) {
            return <Error />;
        }

        return (
            <View>
                {loading && <RouteLoadSpinner />}
                {spotlightBadge && (
                    <View style={styles.spotlightContainer}>
                        <BadgeSpotlight badge={spotlightBadge} />
                    </View>
                )}
                <div id="tab-content-achievements" className="lihp-badges" />
            </View>
        );
    }
}

const styles = StyleSheet.create({
    spotlightContainer: {
        marginBottom: 24,
    },
});

type ExportProps = WithoutApollo<PropsFor<typeof BadgesViewImpl>>;

const BadgesView = withApollo(
    BadgesViewImpl,
) as React.ComponentType<ExportProps>;
export default BadgesView;
